import { useEffect, useRef, useState, type ComponentType } from 'react';
import { LayoutGrid } from 'lucide-react';

type Props = {
  onSignIn: () => Promise<void>;
  message?: string | null;
};

export const SignIn = ({ onSignIn, message }: Props) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Pick a random backdrop on mount so each page load looks different.
  const [Backdrop] = useState<ComponentType>(() => {
    const variants: ComponentType[] = [TilesBackdrop, LogosBackdrop, ArrowsBackdrop];
    const picked = variants[Math.floor(Math.random() * variants.length)];
    return picked ?? TilesBackdrop;
  });

  const handle = async () => {
    setBusy(true);
    setError(null);
    try {
      await onSignIn();
    } catch (err: unknown) {
      const e = err as { errorCode?: string; message?: string };
      if (e.errorCode === 'user_cancelled' || e.errorCode === 'access_denied') {
        setBusy(false);
        return;
      }
      setError(e.message ?? 'Sign-in failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden bg-gradient-to-br from-brand-navy-dark via-brand-navy to-brand-navy-light p-4">
      {/* Radial orange glow */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute left-1/2 top-1/2 h-[680px] w-[680px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-orange/25 blur-3xl" />
      </div>

      {/* Subtle dot grid */}
      <div
        className="pointer-events-none absolute inset-0 opacity-20"
        style={{
          backgroundImage:
            'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.35) 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
        aria-hidden="true"
      />

      {/* Random per-load brand backdrop */}
      <Backdrop />

      {/* Card */}
      <div className="signin-card relative w-full max-w-sm">
        <div className="rounded-2xl border border-white/10 bg-white p-8 shadow-2xl shadow-black/30">
          <div className="flex flex-col items-center gap-2.5">
            <img
              src="/brand/alphadirect-logo.png"
              alt="Alpha Direct Insurance"
              className="h-14 w-auto"
            />
            <div className="flex items-center gap-2">
              <div className="h-px w-8 bg-brand-orange/40" />
              <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-brand-orange">
                OneDesk
              </span>
              <div className="h-px w-8 bg-brand-orange/40" />
            </div>
          </div>

          <div className="mt-6 text-center">
            <div className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-navy-pale">
              <LayoutGrid className="h-6 w-6 text-brand-navy" />
            </div>
            <h1 className="mt-3 text-lg font-bold text-brand-navy">Your launchpad</h1>
            <p className="mt-1.5 text-xs text-text-secondary">
              One sign-in. Every Alpha Direct app.
            </p>
          </div>

          {message && (
            <div
              role="status"
              className="mt-5 rounded-md bg-status-warning/10 p-3 text-xs text-status-warning"
            >
              {message}
            </div>
          )}

          <button
            type="button"
            onClick={handle}
            disabled={busy}
            className="mt-6 flex h-11 w-full items-center justify-center gap-3 rounded-xl bg-brand-navy px-4 text-sm font-semibold text-white shadow-md transition-all hover:bg-brand-navy-light hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy ? (
              <Spinner />
            ) : (
              <>
                <MicrosoftLogo />
                <span>Sign in with Microsoft 365</span>
              </>
            )}
          </button>

          {error && (
            <div
              role="alert"
              className="mt-4 rounded-md bg-status-critical/10 p-3 text-xs text-status-critical"
            >
              {error}
            </div>
          )}

          <p className="mt-5 text-center text-[11px] leading-relaxed text-text-tertiary">
            Access restricted to{' '}
            <span className="font-medium text-brand-navy">@alphadirect.co.bw</span>{' '}
            and partner accounts.
          </p>
        </div>

        <p className="mt-6 text-center text-[11px] text-white/40">
          © {new Date().getFullYear()} Alpha Direct Insurance Co. · OneDesk
        </p>
      </div>
    </div>
  );
};

// ── Shared canvas plumbing ────────────────────────────────────────────

const rand = (lo: number, hi: number) => lo + Math.random() * (hi - lo);
const reduceMotionQuery = () =>
  typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

type RenderArgs = {
  ctx: CanvasRenderingContext2D;
  w: number;
  h: number;
  dt: number; // seconds since last frame
};

/**
 * Boilerplate: set up canvas (DPR-aware), run a RAF loop that calls onFrame,
 * pause on tab hide, honour prefers-reduced-motion (single static frame),
 * resize on viewport changes. Each backdrop just supplies its onFrame +
 * onResize hooks.
 */
const useCanvasLoop = (
  canvasRef: React.RefObject<HTMLCanvasElement>,
  onFrame: (args: RenderArgs) => void,
  onResize: (w: number, h: number) => void,
) => {
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const dpr = window.devicePixelRatio || 1;
    let w = 0;
    let h = 0;

    const resize = () => {
      const rect = canvas.getBoundingClientRect();
      w = Math.max(1, rect.width);
      h = Math.max(1, rect.height);
      canvas.width = Math.floor(w * dpr);
      canvas.height = Math.floor(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      onResize(w, h);
    };
    resize();

    const reduceMotion = reduceMotionQuery();
    if (reduceMotion) {
      onFrame({ ctx, w, h, dt: 0 });
      window.addEventListener('resize', resize);
      return () => window.removeEventListener('resize', resize);
    }

    let raf = 0;
    let last = performance.now();
    let running = true;

    const loop = (now: number) => {
      if (!running) return;
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      onFrame({ ctx, w, h, dt });
      raf = requestAnimationFrame(loop);
    };

    raf = requestAnimationFrame(loop);

    const onVisibility = () => {
      if (document.hidden) {
        running = false;
        cancelAnimationFrame(raf);
      } else if (!running) {
        running = true;
        last = performance.now();
        raf = requestAnimationFrame(loop);
      }
    };

    window.addEventListener('resize', resize);
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', resize);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

const drawRoundedSquare = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  radius: number,
) => {
  const r = Math.min(radius, size / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + size - r, y);
  ctx.quadraticCurveTo(x + size, y, x + size, y + r);
  ctx.lineTo(x + size, y + size - r);
  ctx.quadraticCurveTo(x + size, y + size, x + size - r, y + size);
  ctx.lineTo(x + r, y + size);
  ctx.quadraticCurveTo(x, y + size, x, y + size - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
};

/**
 * The AlphaDirect swoosh — traced from the official Insurance Co.
 * brand mark: a long gentle dip through the middle (concave-up) with
 * a sharp upswing on the right ending in a prominent triangular
 * arrowhead. The asymmetric control points are deliberate — both sit
 * deep below baseline but the second one is held lower so the right
 * side rises more steeply than the left enters.
 *
 * Drawn at origin; the rightmost arrowhead tip ends around (38, -22).
 */
const drawSwoosh = (
  ctx: CanvasRenderingContext2D,
  scale: number,
  rgba: string,
  thickness: number,
) => {
  ctx.save();
  ctx.scale(scale, scale);
  ctx.strokeStyle = rgba;
  ctx.fillStyle = rgba;
  ctx.lineWidth = thickness;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // Body — long gentle dip then sharp rise.
  ctx.beginPath();
  ctx.moveTo(-42, -2);
  ctx.bezierCurveTo(-18, 18, 8, 18, 28, -8);
  ctx.stroke();
  // Prominent triangular arrowhead pointing up-and-right at the same
  // angle as the curve's exit tangent (~52° above horizontal).
  ctx.beginPath();
  ctx.moveTo(38, -22); // apex
  ctx.lineTo(34, -6); // lower-right barb (back along the tangent)
  ctx.lineTo(24, -14); // upper-left barb
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

// AD watermark colour palette — picked once per tile/logo at spawn.
const BRAND_TINTS: Array<{ rgb: string; weight: number }> = [
  { rgb: '255,255,255', weight: 5 }, // white — most common
  { rgb: '255,102,0', weight: 3 }, // brand-orange
  { rgb: '232,233,245', weight: 2 }, // brand-navy-pale
];

const pickBrandTint = (): string => {
  const total = BRAND_TINTS.reduce((sum, t) => sum + t.weight, 0);
  let r = Math.random() * total;
  for (const t of BRAND_TINTS) {
    r -= t.weight;
    if (r <= 0) return t.rgb;
  }
  return BRAND_TINTS[0]!.rgb;
};

// ── Backdrop 1: Kinetic tiles ─────────────────────────────────────────

type Tile = {
  x: number;
  y: number;
  size: number;
  vx: number;
  vy: number;
  rot: number;
  rotV: number;
  showAd: boolean;
  adPhase: number;
  adSpeed: number;
  adRgb: string;
};

const TILE_COUNT = 14;

const spawnTile = (w: number, h: number): Tile => {
  const size = rand(60, 120);
  return {
    x: rand(0, Math.max(1, w - size)),
    y: rand(0, Math.max(1, h - size)),
    size,
    vx: rand(-28, 28),
    vy: rand(-28, 28),
    rot: rand(0, Math.PI * 2),
    rotV: rand(-0.35, 0.35),
    showAd: Math.random() < 0.6,
    adPhase: rand(0, Math.PI * 2),
    adSpeed: rand(0.4, 1.1),
    adRgb: pickBrandTint(),
  };
};

const TilesBackdrop = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const tiles = useRef<Tile[]>([]);

  useCanvasLoop(
    canvasRef,
    ({ ctx, w, h, dt }) => {
      // Integrate.
      for (const t of tiles.current) {
        t.x += t.vx * dt;
        t.y += t.vy * dt;
        t.rot += t.rotV * dt;
        t.adPhase += t.adSpeed * dt;
        if (t.x < 0) {
          t.x = 0;
          t.vx = Math.abs(t.vx);
          t.rotV += rand(-0.2, 0.2);
        } else if (t.x + t.size > w) {
          t.x = w - t.size;
          t.vx = -Math.abs(t.vx);
          t.rotV += rand(-0.2, 0.2);
        }
        if (t.y < 0) {
          t.y = 0;
          t.vy = Math.abs(t.vy);
          t.rotV += rand(-0.2, 0.2);
        } else if (t.y + t.size > h) {
          t.y = h - t.size;
          t.vy = -Math.abs(t.vy);
          t.rotV += rand(-0.2, 0.2);
        }
      }

      // Pairwise collisions (treat tiles as circles of radius=size/2).
      for (let i = 0; i < tiles.current.length; i++) {
        const a = tiles.current[i]!;
        for (let j = i + 1; j < tiles.current.length; j++) {
          const b = tiles.current[j]!;
          const dx = b.x + b.size / 2 - (a.x + a.size / 2);
          const dy = b.y + b.size / 2 - (a.y + a.size / 2);
          const dist = Math.hypot(dx, dy) || 0.0001;
          const minDist = (a.size + b.size) / 2;
          if (dist < minDist) {
            const nx = dx / dist;
            const ny = dy / dist;
            const closing = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
            if (closing < 0) {
              a.vx += closing * nx;
              a.vy += closing * ny;
              b.vx -= closing * nx;
              b.vy -= closing * ny;
              a.rotV += rand(-0.4, 0.4);
              b.rotV += rand(-0.4, 0.4);
            }
            const overlap = (minDist - dist) / 2;
            a.x -= nx * overlap;
            a.y -= ny * overlap;
            b.x += nx * overlap;
            b.y += ny * overlap;
          }
        }
      }

      for (const t of tiles.current) {
        if (t.rotV > 0.35) t.rotV = 0.35;
        if (t.rotV < -0.35) t.rotV = -0.35;
      }

      // Render.
      ctx.clearRect(0, 0, w, h);
      for (const t of tiles.current) {
        ctx.save();
        ctx.translate(t.x + t.size / 2, t.y + t.size / 2);
        ctx.rotate(t.rot);

        ctx.fillStyle = 'rgba(255,255,255,0.07)';
        ctx.strokeStyle = 'rgba(255,255,255,0.10)';
        ctx.lineWidth = 1;
        drawRoundedSquare(ctx, -t.size / 2, -t.size / 2, t.size, 12);
        ctx.fill();
        ctx.stroke();

        if (t.showAd) {
          const pulse = (Math.sin(t.adPhase) + 1) / 2;
          const alpha = pulse * 0.22;
          if (alpha > 0.015) {
            ctx.fillStyle = `rgba(${t.adRgb},${alpha.toFixed(3)})`;
            ctx.font = `700 ${Math.round(t.size * 0.35)}px 'Book Antiqua', Georgia, serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText('AD', 0, 0);
          }
        }

        ctx.restore();
      }
    },
    (w, h) => {
      if (tiles.current.length === 0) {
        tiles.current = Array.from({ length: TILE_COUNT }, () => spawnTile(w, h));
      } else {
        // Resize: keep tiles inside new bounds.
        for (const t of tiles.current) {
          t.x = Math.min(Math.max(t.x, 0), Math.max(0, w - t.size));
          t.y = Math.min(Math.max(t.y, 0), Math.max(0, h - t.size));
        }
      }
    },
  );

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
    />
  );
};

// ── Backdrop 2: AD logo field ─────────────────────────────────────────
// Drifting AD monograms (white "A" + orange "D" with the swoosh
// underneath). Bounces off walls. No pairwise collision so they can
// pass through each other gracefully.

type Logo = {
  x: number;
  y: number;
  scale: number; // 0.5..1.4
  vx: number;
  vy: number;
  rot: number;
  rotV: number;
  fadePhase: number;
  fadeSpeed: number;
};

const LOGO_COUNT = 9;

const spawnLogo = (w: number, h: number): Logo => {
  const margin = 60;
  return {
    x: rand(margin, Math.max(margin + 1, w - margin)),
    y: rand(margin, Math.max(margin + 1, h - margin)),
    scale: rand(0.55, 1.35),
    vx: rand(-20, 20),
    vy: rand(-20, 20),
    rot: rand(-0.25, 0.25),
    rotV: rand(-0.08, 0.08),
    fadePhase: rand(0, Math.PI * 2),
    fadeSpeed: rand(0.3, 0.8),
  };
};

/**
 * Draw the AD monogram + swoosh at origin. Caller handles transform.
 * Designed to read clearly at scales 0.5..1.5.
 *
 * Layout:
 *   AD letters centred around y=-12 (top of composition)
 *   swoosh translated to y=+22 so it sits visibly UNDER the letters,
 *   not overlapping them.
 */
const drawAdMonogram = (ctx: CanvasRenderingContext2D, alpha: number) => {
  const a = alpha.toFixed(3);
  // White "A".
  ctx.fillStyle = `rgba(255,255,255,${a})`;
  ctx.font = `700 56px 'Book Antiqua', Georgia, serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('A', -16, -12);
  // Orange "D".
  ctx.fillStyle = `rgba(255,102,0,${a})`;
  ctx.fillText('D', 16, -12);
  // Swoosh sits below the AD letters and spans their full width.
  ctx.save();
  ctx.translate(2, 24);
  drawSwoosh(ctx, 0.95, `rgba(255,255,255,${a})`, 3.2);
  ctx.restore();
};

/**
 * A simple straight arrow pointing along +X. Used by the flying-arrows
 * backdrop where rotation is set from the velocity vector — keeping the
 * tip on the X-axis means the arrowhead always leads the direction of
 * travel after rotation. Length ≈ 60 units, height ≈ 14 units.
 */
const drawArrow = (
  ctx: CanvasRenderingContext2D,
  scale: number,
  rgba: string,
  thickness: number,
) => {
  ctx.save();
  ctx.scale(scale, scale);
  ctx.strokeStyle = rgba;
  ctx.fillStyle = rgba;
  ctx.lineWidth = thickness;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  // Shallow swoosh tail along +X — gives the arrow a hint of curvature
  // (echoing the AD swoosh) without throwing the leading direction off.
  ctx.beginPath();
  ctx.moveTo(-30, 4);
  ctx.quadraticCurveTo(-5, -2, 22, 0);
  ctx.stroke();
  // Filled arrowhead, tip on the X-axis.
  ctx.beginPath();
  ctx.moveTo(32, 0);
  ctx.lineTo(20, -7);
  ctx.lineTo(22, 0);
  ctx.lineTo(20, 7);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
};

const LogosBackdrop = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const logos = useRef<Logo[]>([]);

  useCanvasLoop(
    canvasRef,
    ({ ctx, w, h, dt }) => {
      ctx.clearRect(0, 0, w, h);
      for (const l of logos.current) {
        l.x += l.vx * dt;
        l.y += l.vy * dt;
        l.rot += l.rotV * dt;
        l.fadePhase += l.fadeSpeed * dt;

        // Bounce off walls (loosely — based on logo footprint).
        const reach = 50 * l.scale;
        if (l.x < reach || l.x > w - reach) {
          l.vx *= -1;
          l.x = Math.min(Math.max(l.x, reach), w - reach);
        }
        if (l.y < reach || l.y > h - reach) {
          l.vy *= -1;
          l.y = Math.min(Math.max(l.y, reach), h - reach);
        }

        const pulse = (Math.sin(l.fadePhase) + 1) / 2;
        const alpha = 0.06 + pulse * 0.16; // 0.06..0.22

        ctx.save();
        ctx.translate(l.x, l.y);
        ctx.rotate(l.rot);
        ctx.scale(l.scale, l.scale);
        drawAdMonogram(ctx, alpha);
        ctx.restore();
      }
    },
    (w, h) => {
      if (logos.current.length === 0) {
        logos.current = Array.from({ length: LOGO_COUNT }, () => spawnLogo(w, h));
      }
    },
  );

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
    />
  );
};

// ── Backdrop 3: Flying arrows ─────────────────────────────────────────
// Swoosh-arrows flowing across the viewport in random directions. Each
// arrow's rotation matches its velocity, so it always points where it's
// going. Arrows wrap around the edges (no bounce) for a sense of
// continuous flow.

type Arrow = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  scale: number;
  rgba: string;
};

const ARROW_COUNT = 18;

const ARROW_PALETTE = [
  'rgba(255,255,255,0.55)',
  'rgba(255,255,255,0.35)',
  'rgba(255,102,0,0.65)', // brand orange — accent
  'rgba(232,233,245,0.45)', // navy-pale
];

const spawnArrow = (w: number, h: number): Arrow => {
  const angle = rand(0, Math.PI * 2);
  const speed = rand(40, 90);
  return {
    x: rand(0, w),
    y: rand(0, h),
    vx: Math.cos(angle) * speed,
    vy: Math.sin(angle) * speed,
    scale: rand(0.5, 1.1),
    rgba: ARROW_PALETTE[Math.floor(Math.random() * ARROW_PALETTE.length)]!,
  };
};

const ArrowsBackdrop = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const arrows = useRef<Arrow[]>([]);

  useCanvasLoop(
    canvasRef,
    ({ ctx, w, h, dt }) => {
      ctx.clearRect(0, 0, w, h);
      const margin = 60; // wrap-around buffer so arrows don't pop
      for (const a of arrows.current) {
        a.x += a.vx * dt;
        a.y += a.vy * dt;

        // Wrap.
        if (a.x < -margin) a.x = w + margin;
        else if (a.x > w + margin) a.x = -margin;
        if (a.y < -margin) a.y = h + margin;
        else if (a.y > h + margin) a.y = -margin;

        // Render: rotation aligned with velocity so the arrowhead
        // always leads the direction of travel.
        const angle = Math.atan2(a.vy, a.vx);
        ctx.save();
        ctx.translate(a.x, a.y);
        ctx.rotate(angle);
        drawArrow(ctx, a.scale, a.rgba, 3);
        ctx.restore();
      }
    },
    (w, h) => {
      if (arrows.current.length === 0) {
        arrows.current = Array.from({ length: ARROW_COUNT }, () => spawnArrow(w, h));
      }
    },
  );

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none absolute inset-0 h-full w-full"
      aria-hidden="true"
    />
  );
};

// ── Card-internal helpers ─────────────────────────────────────────────

const MicrosoftLogo = () => (
  <svg viewBox="0 0 21 21" className="h-4 w-4" aria-hidden="true">
    <rect x="1" y="1" width="9" height="9" fill="#F25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
    <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
    <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
  </svg>
);

const Spinner = () => (
  <svg
    className="h-5 w-5 animate-spin"
    viewBox="0 0 24 24"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.3" />
    <path
      d="M4 12a8 8 0 0 1 8-8"
      stroke="currentColor"
      strokeWidth="3"
      strokeLinecap="round"
    />
  </svg>
);
